import { getEnv } from '@unsw/config'
import type { Token, TokenResponse } from '@unsw/types'
import { Mutex } from 'async-mutex'
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'

/**
 * Auth store interface for token management
 * This allows the store to be injected without circular dependencies
 */
interface AuthStoreInterface {
  getAccessToken: () => string | null
  getRefreshToken: () => string | null
  isAccessTokenExpired: () => boolean
  isRefreshTokenExpired: () => boolean
  setAccessToken: (token: Token) => void
  setTokens: (tokens: TokenResponse | null) => void
  logout: () => void
}

let authStore: AuthStoreInterface | null = null

// Mutex to prevent multiple concurrent refresh requests
const refreshMutex = new Mutex()

/**
 * Set the auth store reference
 * Call this once in your app setup to connect the auth store
 *
 * @example
 * // In your app's main.tsx
 * import { setAuthStore } from '@unsw/api/client'
 * import { useAuthStore } from '@unsw/store/auth'
 *
 * setAuthStore(useAuthStore.getState())
 */
export function setAuthStore(store: AuthStoreInterface): void {
  authStore = store
}

/**
 * Refresh the access token using the refresh token
 */
async function refreshAccessToken(): Promise<boolean> {
  if (!authStore) return false

  const refreshToken = authStore.getRefreshToken()
  if (!refreshToken) return false

  // Check if refresh token is expired
  if (authStore.isRefreshTokenExpired()) {
    console.warn('[API] Refresh token expired, logging out')
    authStore.logout()
    return false
  }

  try {
    const env = getEnv()
    const response = await axios.post<TokenResponse>(
      `${env.API_BASE_URL}/api/v1/sdk/auth/refresh`,
      {},
      {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
          'Content-Type': 'application/json',
        },
      },
    )

    if (response.data?.access_token) {
      authStore.setAccessToken(response.data.access_token)
      console.log('[API] Access token refreshed successfully')
      return true
    }

    return false
  } catch (error) {
    console.error('[API] Failed to refresh token:', error)
    authStore.logout()
    return false
  }
}

/**
 * Create a base API client with auth interceptor and refresh token support
 */
export function createApiClient(config?: AxiosRequestConfig): AxiosInstance {
  const env = getEnv()

  const client = axios.create({
    baseURL: env.API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
    ...config,
  })

  // Request interceptor - check token expiry and refresh if needed
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      if (!authStore) return config

      // Check if access token is expired and needs refresh
      if (authStore.isAccessTokenExpired() && authStore.getRefreshToken()) {
        // Use mutex to prevent multiple concurrent refresh requests
        if (!refreshMutex.isLocked()) {
          const release = await refreshMutex.acquire()
          try {
            await refreshAccessToken()
          } finally {
            release()
          }
        } else {
          // Wait for the ongoing refresh to complete
          await refreshMutex.waitForUnlock()
        }
      }

      // Add the (potentially refreshed) access token
      const token = authStore.getAccessToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }

      return config
    },
    (error) => Promise.reject(error),
  )

  // Response interceptor - handle 401 errors
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config

      // If we get a 401 and haven't retried yet, try to refresh the token
      if (error.response?.status === 401 && !originalRequest._retry && authStore) {
        originalRequest._retry = true

        // Use mutex to prevent multiple concurrent refresh requests
        if (!refreshMutex.isLocked()) {
          const release = await refreshMutex.acquire()
          try {
            const refreshed = await refreshAccessToken()
            if (refreshed) {
              // Retry the original request with new token
              const token = authStore.getAccessToken()
              if (token) {
                originalRequest.headers.Authorization = `Bearer ${token}`
              }
              return client(originalRequest)
            }
          } finally {
            release()
          }
        } else {
          // Wait for the ongoing refresh to complete and retry
          await refreshMutex.waitForUnlock()
          const token = authStore.getAccessToken()
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return client(originalRequest)
          }
        }
      }

      return Promise.reject(error)
    },
  )

  return client
}

// Default client instance
export const apiClient = createApiClient()

// Re-export for convenience
export { axios }
export type { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
